/**
 * Section processing and creation logic
 * Matches old Backbone getSections() pattern - rates components FIRST before creating sections
 */
import {
  IBotSection,
  IBotData,
  IAHUParams,
  ICoilParams,
  IIdNameValue,
  IErWheelParams,
} from '../../../models';
import {
  DEFAULT_COEFFICIENTS,
  SectionModelData,
  SectionWithModelData,
} from '../../../models/bot';
import { dispatchMutation, dispatchQuery } from '../../../utils/dispatch.utils';
import { ahuApi, filterApi } from '../../../services';
import { generateTimestamp } from '../../index';
// ============================================================================
// ENERGY WHEEL / RHE CONSTANTS AND DEFAULTS
// ============================================================================
export const MFG = {
  52: 'heatex',
  233: 'airotor',
  60: 'innergy_tech',
} as const;
type MfgType = 52 | 233 | 60;

export function buildRheDefaults(
  mfg: MfgType,
  userInput: Record<string, any>,
  tunnel_configuration: number,
) {
  const defaults = { ...RHE_DEFAULTS_BY_MFG[mfg] };
  if (!defaults) {
    throw new Error(`Unsupported manufacturer: ${mfg}`);
  }
  if (tunnel_configuration === 4) {
    defaults.configuration = '2';
    defaults.configuration_id = 2;
    if (mfg === 233) {
      defaults.purge_sector_location = 'no_purge_sector';
      defaults.purge_sector_location_id = 1;
      defaults.purge_angle = '0';
      defaults.purge_angle_id = 1;
    }
  }

  return {
    manufacturer: MFG[mfg],
    part: 'erwheel',
    input: {
      ...defaults,
      ...userInput, // user values always win
    },
  };
}

const RHE_DEFAULTS_BY_MFG: Record<MfgType, any> = {
  52: {
    mfg: 'Heatex',
    desiccant: 'molecular_sieve_3a',
    rotor_depth: '7.87',
    rotor_depth_id: 2,
    wheel_size: null,
    speed: 25,
    purge_sector_location: 'no_purge_sector',
    purge_sector_location_id: 1,
    mtr_service_door: 'none',
    pricing_method: 1,
    purge_angle: '0',
    purge_angle_id: 1,
    media_edge: 'none',
    eatr_oacf: true,
    altitude: 0,
    bottom_plate: 'none',
    fan_locations: 'eadt_oadt',
    hub: 'ball_bearing_with_shaft',
    seal: 'brush_seal',
    fin_pattern: 'standard',
    fin_thickness: 'standard',
    well_height: '0.071',
    casing: 'standard',
    casing_type: 'standard_casing',
    speed_control: 'constant_drive',
    drive_belt: 'power_belt',
    certification: 'ahri1060',
    configuration: '1',
    configuration_id: 1,
    mounting_arr: [{ id: 1, name: 'A', value: 'a' }],
    flow_type: 'counter',
    instl_position: 'vertical',
    temp_control: 'none',
    rotation_detector: 'none',
    coating_frame: 'none',
    coating_media: 'none',
    min_face_velocity: 200,
    max_face_velocity: 800,
    oa_air_stream_winter: 'supply',
    ra_air_stream_winter: 'exhaust',

    oa_sp: 0,
    oa_db_temp_winter: 0,
    oa_wb_temp_winter: -5,
    ra_sp: 0,
    ra_db_temp_winter: 75,
    ra_wb_temp_winter: 65,
    difference_in_pressure_winter: 1.5,

    oa_air_stream_summer: 'supply',
    oa_db_temp_summer: 95,
    oa_rh_summer: 80,
    oa_wb_temp_summer: 75,
    ra_wb_temp_summer: 55,
    ra_air_stream_summer: 'exhaust',
    ra_db_temp_summer: 72,
    difference_in_pressure_summer: 1.5,
  },
  233: {
    mfg: 'AI Rotor',
    desiccant: 'molecular_sieve_3a',
    rotor_depth: null,
    hub: 'standard',
    seal: 'standard_seal',
    fin_pattern: 'standard',
    fin_thickness: 'f70',
    well_height: '1.7',
    casing: 'standard',
    casing_type: 'standard_casing',
    speed_control: 'vfd',
    drive_belt: 'standard_belt',
    mtr_service_door: 'none',
    certification: 'ahri1060',
    configuration: '1',
    configuration_id: 1,
    fan_locations: 'eadt_oadt',
    media_edge: 'none',
    mounting_arr: [{ id: 1, name: 'A', value: 'a' }],
    flow_type: 'counter',
    instl_position: 'vertical',
    temp_control: 'none',
    rotation_detector: 'none',
    coating_frame: 'none',
    coating_media: 'none',
    pricing_method: 1,
    purge_angle: '5',
    purge_angle_id: 6,
    purge_sector_location: 'vertical_purge_down',
    purge_sector_location_id: 6,
    min_face_velocity: 200,
    max_face_velocity: 800,
    oa_air_stream_winter: 'supply',
    ra_air_stream_winter: 'return',

    oa_sp: 0,
    oa_db_temp_winter: 0,
    oa_rh_winter: 15,
    ra_sp: 0,
    ra_db_temp_winter: 75,
    ra_rh_winter: 55,
    difference_in_pressure_winter: 1.5,

    oa_air_stream_summer: 'supply',
    oa_db_temp_summer: 95,
    oa_rh_summer: 80,
    ra_air_stream_summer: 'return',
    ra_db_temp_summer: 72,
    ra_rh_summer: 55,
    difference_in_pressure_summer: 1.5,

    rotor_depth_id: null,
    speed: 20,
    wheel_size: null,
    eatr_oacf: 0,
    altitude: 0,
    bottom_plate: 'none',
  },
  60: {
    mfg: 'Innergytech',
    desiccant: 'molecular_sieve_3a',
    rotor_depth: null,
    hub: 'standard',
    seal: 'airloop_labyrinth_seal',
    fin_pattern: 'high_performance',
    fin_thickness: 'standard',
    well_height: '1.7',
    casing: 'standard',
    casing_type: 'standard_casing',
    speed_control: 'vfd',
    drive_belt: 'standard_belt',
    mtr_service_door: 'none',
    certification: 'ahri1060',
    configuration: '1',
    configuration_id: 1,
    fan_locations: 'eadt_oadt',
    media_edge: 'none',
    mounting_arr: 'a',
    flow_type: 'counter',
    instl_position: 'vertical',
    temp_control: 'none',
    rotation_detector: 'none',
    coating_frame: 'none',
    coating_media: 'none',
    pricing_method: 1,
    purge_angle: '0',
    purge_angle_id: 1,
    purge_sector_location: 'no_purge_sector',
    purge_sector_location_id: 1,
    min_face_velocity: 200,
    max_face_velocity: 800,
    oa_air_stream_winter: 'supply',
    ra_air_stream_winter: 'return',

    oa_sp: 0,
    oa_db_temp_winter: 0,
    oa_rh_winter: 15,
    ra_sp: 0,
    ra_db_temp_winter: 75,
    ra_rh_winter: 55,
    difference_in_pressure_winter: 1.5,

    oa_air_stream_summer: 'supply',
    oa_db_temp_summer: 95,
    oa_rh_summer: 80,
    ra_air_stream_summer: 'return',
    ra_db_temp_summer: 72,
    ra_rh_summer: 55,
    difference_in_pressure_summer: 1.5,

    rotor_depth_id: null,
    speed: 20,
    wheel_size: null,
    eatr_oacf: 0,
    altitude: 0,
    bottom_plate: 'none',
  },
};
// ============================================================================
// FILTER RATING FUNCTIONS
// ============================================================================
/**
 * Gets filter frame for a filter (matches old Backbone get_filter_frame)
 */
async function getFilterFrame(
  filters: any[],
  getOptions: () => { signal?: AbortSignal },
): Promise<{ frame: any; filter: any }> {
  let frame = null;
  let filter = filters[0];
  let count = 0;
  // Try up to 10 filters to find one with a frame
  do {
    try {
      const frames = (await dispatchQuery(
        filterApi.endpoints.getFilterFrames,
        filters[count].id,
        getOptions(),
      )) as any[];

      if (frames && frames.length > 0) {
        frame = frames[0];
        filter = filters[count];
        break;
      }
    } catch (e) {
      console.error('Error fetching filter frame:', {
        filterId: filters[count]?.id,
        error: e,
        message: e instanceof Error ? e.message : String(e),
      });
      // Continue to next filter
    }
    count++;
  } while (count < 10 && count < filters.length && !frame);
  return { frame, filter };
}
/**
 * Rates a filter section (matches old Backbone get_filter)
 * Called FIRST before creating section
 */
export async function rateFilter(
  section: IBotSection,
  ahuParamsResult: any,
  nextSectionType: string,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<SectionModelData> {
  const hasFrame = nextSectionType !== 'filter';
  const sizeQtyId = ahuParamsResult?.filter?.size_qty_id ?? 1;

  const buildParams = (withFrame: boolean) => {
    const params = new URLSearchParams({
      type_id: String(section.type_id ?? 7),
      merv_id: String(section.merv_id ?? 8),
      depth_nominal_id: String(section.depth_nominal_id ?? 2),
      bank_configuration_id: String(sizeQtyId),
      cms_id: '1',
      page: '1',
    });

    if (withFrame) {
      params.append('frame', 'true');
    }

    return params.toString();
  };

  const fetchFilters = async (withFrame: boolean) =>
    (await dispatchQuery(
      filterApi.endpoints.getFilters,
      buildParams(withFrame),
      getOptions(),
    )) as any[];

  // Initial request
  const filters = await fetchFilters(hasFrame);

  onProgress?.(DEFAULT_COEFFICIENTS.filter_rate);

  // No filters found
  if (!filters?.length) {
    if (hasFrame) {
      const filtersWithoutFrame = await fetchFilters(false);

      if (filtersWithoutFrame?.length) {
        return {
          model: filtersWithoutFrame[0],
          frame: null,
          error: null,
        };
      }
    }

    return {
      model: null,
      error: 'No suitable filter available',
    };
  }

  // Filter without frame
  if (!hasFrame) {
    return {
      model: {
        ...filters[0],
        size_qty_id: sizeQtyId,
        error: null,
      },
    };
  }

  // Filter with frame
  const { frame, filter } = await getFilterFrame(filters, getOptions);

  return {
    model: {
      ...filter,
      size_qty_id: sizeQtyId,
    },
    frame,
    error: null,
  };
}
// ============================================================================
// COIL RATING FUNCTIONS
// ============================================================================
/**
 * Default temperature values for different coil types and duties
 */
const COIL_DEFAULTS = {
  fluid_cooling: {
    eadb_temp: 80.0,
    eawb_temp: 67.0,
    desired_ladb_temp: 55.0,
    desired_lawb_temp: 54.0,
    entering_fluid_temp: 45.0,
    desired_leaving_fluid_temp: 65.0,
  },
  fluid_heating: {
    eadb_temp: 45.0,
    desired_ladb_temp: 85.0,
    entering_fluid_temp: 180.0,
    desired_leaving_fluid_temp: 140.0,
  },
  evaporator_cooling: {
    eadb_temp: 80.0,
    eawb_temp: 67.0,
    desired_ladb_temp: 55.0,
    desired_lawb_temp: 54.0,
    suction_temp: 45,
    ref_liquid_temp: 110,
  },
  steam_heating: {
    eadb_temp: 45.0,
    desired_ladb_temp: 85.0,
  },
} as const;
/**
 * Coil type mappings
 */
const COIL_TYPES: { [key: number]: string } = {
  1: 'fluid',
  2: 'evaporator',
  3: 'steam',
};
/**
 * Coil duty mappings
 */
const COIL_DUTIES: { [key: number]: string } = {
  1: 'cooling',
  2: 'heating',
};
function collectCoilManufacturerData(
  manufacturer: string,
  withPerformanceParams: boolean,
  rows: string,
  fpi: string,
  circuiting: string,
) {
  if (manufacturer === 'rae') {
    return {
      manufacturer: 'rae',
      input: {
        mfg_id: '27',
        mfg: 'RAE',
        fin_thick: '0.0080',
        fin_thick_id: 4,
        tube_dia: '0.625',
        rows: withPerformanceParams ? rows : '--',
        fpi: withPerformanceParams ? fpi : '--',
        circuiting: withPerformanceParams ? circuiting : '--',
      },
    };
  }

  if (manufacturer === 'modine') {
    return {
      manufacturer: 'modine',
      input: {
        mfg_id: '79',
        mfg: 'modine',
        fin_thick: '0.0060',
        fin_thick_id: 2,
        tube_dia: '5/8',
        casing_mtl_gauge: 'steel_ghd_g',
        casing_type: 'flanged',
        fin_pattern: 'flat',
        tube_mtl: 'copper',
        rows: withPerformanceParams ? rows : '--',
        fpi: withPerformanceParams ? fpi : '--',
        circuiting: withPerformanceParams ? circuiting : '--',
        connection_per_header: '1',
        tube_wall_thick: '0.020',
        tube_hor_spacing_id: 3,
        tube_vert_spacing_id: 3,
        fin_pattern_id: 1,
        conn_rotation: 'None',
      },
    };
  }

  // coilmaster
  return {
    manufacturer: 'coilmaster',
    input: {
      mfg_id: '21',
      mfg: 'Coilmaster',
      ...(withPerformanceParams && {
        rows,
        fpi,
        circuiting,
      }),
      ...(!withPerformanceParams && {
        rows: '--',
        fpi: '--',
        circuiting: '--',
      }),
    },
  };
}
/**
 * Builds coil rating data structure
 */
function buildCoilRatingData(
  section: IBotSection,
  _botData: IBotData,
  coilParams: any,
  params: ICoilParams,
  connHands: IIdNameValue[],
  sizerSettings: any,
  defaults: any,
): any {
  const type = COIL_TYPES[section.type_id || 1];
  const duty = COIL_DUTIES[section.duty_id || 1];
  const sa_volume_max = +(section.airflow || 0);
  const casingFlangeId =
    section.duty_id === 2
      ? sizerSettings?.hc_top_and_btm_casing_flange_id
      : sizerSettings?.cc_top_and_btm_casing_flange_id;
  const casingFlange =
    section.duty_id === 2
      ? sizerSettings?.hc_top_and_btm_casing_flange
      : sizerSettings?.cc_top_and_btm_casing_flange;
  const conn_hand =
    connHands.find((e: IIdNameValue) => e.id == section.conn_hand_id)?.value ||
    'right';
  const htfluid =
    section.htfluid ||
    params.htfluid.find((e: IIdNameValue) => e.id == section.htfluid_id)
      ?.value ||
    'water';

  const data: any = {
    manufacturer: 'coilmaster',
    part: 'coil',
    type,
    input: {
      altitude: '0',
      mfg_id: '21',
      mfg: 'Coilmaster',
      type,
      duty,
      fh: coilParams.fh,
      fl: coilParams.fl,
      qty_stacked: coilParams.qty,
      rows: '--',
      fpi: '--',
      circuiting: '--',
      airflow_actual_total: sa_volume_max,
      air_stream_id: section.air_stream_id,
      face_velocity_actual: coilParams.face_velocity,
      outlet_header_dia: '--',
      outlet_conn_dia: '--',
      inlet_header_dia: '--',
      inlet_conn_dia: '--',
      tube_cleanable: '--',
      tube_mtl: 'copper',
      fin_mtl: 'aluminum',
      fin_finish: 'mill',
      fin_pattern: 'waffle',
      casing_flange_top_id: casingFlangeId || 5,
      casing_flange_btm_id: casingFlangeId || 5,
      casing_flange_top: casingFlange || '1.50',
      casing_flange_btm: casingFlange || '1.50',
      casing_flange_header_side_id: casingFlangeId || 5,
      casing_flange_return_side_id: casingFlangeId || 5,
      casing_flange_header_side: casingFlange || '1.50',
      casing_flange_return_side: casingFlange || '1.50',
      apd_max: '3',
      casing_mtl_gauge: 'steel_ghd_g_hr_solid_12ga',
      casing_type: 'standard',
      casing_stacking_flange: 'false',
      feeds: '0',
      fin_thick: '0.0060',
      fpi_max: '14.00',
      htfluid,
      moisture_eliminator: 'false',
      mounting_holes: 'false',
      tube_dia: '5/8',
      tube_hor_spacing: 1.299,
      tube_hor_spacing_id: '3',
      tube_interior: 'smooth',
      tube_vert_spacing: 1.5,
      tube_vert_spacing_id: '3',
      fluid_pd_max: '20',
      fouling_factor_inside: '0',
      conn_end: 'same_end',
      conn_hand,
      conn_mtl: 'copper',
      conn_pipe_end: 'swt',
      conn_protrusion_from_header: '7',
      tube_wall_thick_id: 11,
      tube_wall_thick: 0.02,
      ...defaults,
    },
  };
  // Heating coil specific settings
  if (section.duty_id === 2) {
    data.input.casing_flange_top_id =
      sizerSettings?.hc_top_and_btm_casing_flange_id || 5;
    data.input.casing_flange_btm_id =
      sizerSettings?.hc_top_and_btm_casing_flange_id || 5;
    data.input.casing_flange_top =
      sizerSettings?.hc_top_and_btm_casing_flange || '1.50';
    data.input.casing_flange_btm =
      sizerSettings?.hc_top_and_btm_casing_flange || '1.50';
  }
  // Evaporator specific settings
  if (type === 'evaporator') {
    data.input.evaporator_mode = 'dx';
    data.input.entering_superheat = '8';
    data.input.style = 'interlaced_2';
    data.input.aux_side_conn = 'false';
    data.input.txv = 'false';
    data.input.txv_instl = 'false';
    data.input.coil_style = 'interlaced';
  }
  // Steam specific settings
  else if (type === 'steam') {
    data.input.entering_superheat = '8';
    data.input.steam_pressure = '5';
    data.input.style = 'steam_std';
    data.input.coil_style = 'steam_std';
    data.input.thermostatic_vent = 'false';
  }
  return data;
}
/**
 * Rates a coil section (matches old Backbone get_coil)
 * Called FIRST before creating section
 */
export async function rateCoil(
  section: IBotSection,
  botData: IBotData,
  ahuParams: IAHUParams,
  coilParamsData: ICoilParams,
  connHands: IIdNameValue[],
  ahuParamsResult: any,
  sizerSettings: any,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<SectionModelData> {
  const type = COIL_TYPES[section.type_id || 1];
  const duty = COIL_DUTIES[section.duty_id || 1];

  const coilParamsKey = `${duty}_coil` as 'cooling_coil' | 'heating_coil';

  const coilParams =
    ahuParamsResult?.[coilParamsKey as keyof typeof ahuParamsResult] ||
    ahuParams?.[coilParamsKey as keyof typeof ahuParams];
  if (!coilParams) {
    return { error: 'Coil parameters not found' };
  }

  const typeKey = `${type}_${duty}`;
  const defaults = COIL_DEFAULTS[typeKey as keyof typeof COIL_DEFAULTS] || {};

  const baseData = buildCoilRatingData(
    section,
    botData,
    coilParams,
    coilParamsData,
    connHands,
    sizerSettings,
    defaults,
  );

  onProgress?.(DEFAULT_COEFFICIENTS.coil_rate);

  const rows = section.duty_id === 2 ? '1' : '8';
  const fpi = '8';
  const circuiting = section.type_id !== 3 ? '1' : '--';

  // 🔥 4-step strategy (ordered!)
  const attempts = [
    { mfg: 'modine', withParams: false },
    { mfg: 'modine', withParams: true },
    { mfg: 'rae', withParams: false },
    { mfg: 'rae', withParams: true },
    //{ mfg: 'coilmaster', withParams: false },
    //{ mfg: 'coilmaster', withParams: true },
  ];

  for (const attempt of attempts) {
    const mfgData = collectCoilManufacturerData(
      attempt.mfg,
      attempt.withParams,
      rows,
      fpi,
      circuiting,
    );
    let htfluid_id = baseData.input.htfluid_id;
    let htfluid = baseData.input.htfluid;
    if (attempt.mfg === 'rae' && type === 'evaporator') {
      htfluid_id = 13;
      htfluid = 'r410a';
    }

    const data = {
      ...baseData,
      manufacturer: mfgData.manufacturer,
      input: {
        ...baseData.input,
        ...mfgData.input,
        htfluid,
        htfluid_id,
      },
    };

    try {
      const resp: any = await dispatchMutation(
        ahuApi.endpoints.rate,
        { body: data },
        getOptions(),
      );

      if (resp?.models?.length && resp.models[0].capacity_sensible !== 'nan') {
        return {
          model: resp.models[0],
          error: null,
          input: data.input,
          defaults,
        };
      }
    } catch (e: any) {
      if (e.statusText === 'abort') throw e;
    }
  }

  return {
    model: null,
    error: `No suitable ${duty} coil available`,
  };
}
// ============================================================================
// ENERGY WHEEL RATING FUNCTIONS
// ============================================================================
/**
 * Rates an energy wheel section (matches pattern for rateFilter/rateCoil)
 * Called FIRST before creating section
 * Only processes energy_wheel sections from the second tunnel (index 1)
 * Energy wheel component has 2 sections, component is created only in second tunnel
 */
export async function rateEnergyWheel(
  section: IBotSection,
  botData: IBotData,
  params: IErWheelParams,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<SectionModelData> {
  // Get manufacturer from section or default to HEATEX
  const mfg = section.mfg_id as 233 | 52 | 60; // Default to HEATEX if not specified or invalid

  // Extract series, type, and rotor_style from section if available
  // These values can be changed by the user in rotary_htex.tsx
  const userInput: Record<string, any> = {};
  const airflow = Number(botData.sa_volume_max);
  const ra_airflow = Number(botData.ea_volume_max);
  userInput.oa_airflow_actual_winter = airflow;
  userInput.ra_airflow_actual_winter = ra_airflow;
  userInput.oa_airflow_actual_summer = airflow;
  userInput.ra_airflow_actual_summer = ra_airflow;
  userInput.mtr_voltage_id = botData.voltage_id;
  userInput.series = params.series.find((s) => s.id === section.series_id);
  userInput.type = params.type.find((t) => t.id === section.type_id)?.value;
  userInput.rotor_style = params.rotor_style.find(
    (t) => t.id === section.rotor_style_id,
  )?.value;
  userInput.rated_datetime = generateTimestamp();
  userInput.mtr_voltage = botData.voltage_name || '460/3/60';

  // Build defaults using buildRheDefaults
  // User values from sectionParams will override manufacturer defaults
  const defaults = buildRheDefaults(
    mfg,
    userInput,
    botData.tunnel_configuration,
  );

  onProgress?.(DEFAULT_COEFFICIENTS.er_wheel_rate);
  const resp = (await dispatchMutation(
    ahuApi.endpoints.rate,
    { body: defaults },
    getOptions(),
  )) as any;

  // Check if we got a valid model
  if (resp?.models?.length && resp.models[0].capacity_sensible !== 'nan') {
    return {
      model: resp.models[0],
      error: null,
      input: defaults.input,
      defaults,
    };
  }
  return {
    model: null,
    error: 'No suitable rotary htex available',
    input: defaults.input,
    defaults,
  };
}
// ============================================================================
// PLENUM FUNCTIONS
// ============================================================================
/**
 * Gets model data for a plenum section (matches old Backbone get_plenum)
 */
export function getPlenumModelData(): SectionModelData {
  return {
    model: null,
    error: null,
  };
}
// ============================================================================
// SECTION PROCESSING (matches old Backbone getSections)
// ============================================================================
/**
 * Processes a single section to get model data
 * Matches old Backbone pattern: calls get_${section.type} for each section
 */
async function processSection(
  section: IBotSection,
  botData: IBotData,
  ahuParams: IAHUParams,
  coilParams: ICoilParams,
  connHands: IIdNameValue[],
  ahuParamsResult: any,
  sizerSettings: any,
  _tunnelIndex: number,
  nextSectionType: string,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<SectionModelData | null> {
  // Fan sections use existing tunnel fan data
  if (section.type === 'fan') {
    return section.component || null;
  }
  // Filter sections - rate first
  if (section.type === 'filter' && section.type_id) {
    return await rateFilter(
      section,
      ahuParamsResult,
      nextSectionType,
      getOptions,
      onProgress,
    );
  }
  // Coil sections - rate first
  if (section.type === 'coil' && section.duty_id) {
    return await rateCoil(
      section,
      botData,
      ahuParams,
      coilParams,
      connHands,
      ahuParamsResult,
      sizerSettings,
      getOptions,
      onProgress,
    );
  }
  // Plenum sections
  if (section.type === 'plenum') {
    return getPlenumModelData();
  }
  return null;
}
/**
 * Processes all sections to get model data (matches old Backbone getSections)
 * Rates filters and coils FIRST before creating sections
 */
export async function processSectionsForModelData(
  validSections: IBotSection[],
  ahuParamsResult: any,
  botData: IBotData,
  ahuParams: IAHUParams,
  coilParams: ICoilParams,
  connHands: IIdNameValue[],
  sizerSettings: any,
  tunnelIndex: number,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
): Promise<SectionWithModelData[]> {
  const sectionsWithModelData: SectionWithModelData[] = [];
  // Process each section (matches old Backbone: for (const section_ of self.sections.models))
  for (const [sectionIndex, section] of validSections.entries()) {
    // Skip sections without type (matches old Backbone: if (!section.type) continue)
    if (!section.type) {
      continue;
    }
    const nextSectionType = validSections[sectionIndex + 1]?.type;
    try {
      // Process section to get model data (matches old Backbone: await self[`get_${section.type}`](section))
      const modelData = await processSection(
        section,
        botData,
        ahuParams,
        coilParams,
        connHands,
        ahuParamsResult,
        sizerSettings,
        tunnelIndex,
        nextSectionType,
        getOptions,
        onProgress,
      );
      // Skip if no model data returned (matches old Backbone: if (!resp) continue)
      if (!modelData) {
        continue;
      }
      // Check for error in model data - stop immediately if error found
      if (modelData.error) {
        // Add section with error and return immediately to stop processing
        sectionsWithModelData.push({ section, modelData });
        return sectionsWithModelData;
      }
      // Add section with model data (matches old Backbone: section_.set("model_data", resp))
      sectionsWithModelData.push({ section, modelData });
    } catch (error) {
      console.error('Error processing section:', {
        sectionType: section.type,
        sectionId: section.id,
        sectionTypeId: section.type_id,
        sectionDutyId: section.duty_id,
        error,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      // Add section with error and return immediately to stop processing
      sectionsWithModelData.push({
        section,
        modelData: { error: 'Failed to process section' },
      });
      return sectionsWithModelData;
    }
  }
  return sectionsWithModelData;
}
/**
 * Checks if there's an error in any section (matches old Backbone error checking)
 */
export function findSectionError(
  sectionsWithModelData: SectionWithModelData[],
): string | null {
  const errorSection = sectionsWithModelData.find((s) => s.modelData?.error);
  return errorSection?.modelData?.error || null;
}
// ============================================================================
// BACKWARD COMPATIBILITY FUNCTIONS
// ============================================================================
/**
 * Gets model data for a coil section (kept for backward compatibility)
 */
export function getCoilModelData(
  section: IBotSection,
  onProgress?: (coefficient: number) => void,
): SectionModelData {
  if (!section.type_id || !section.duty_id) {
    return { error: 'Coil type_id and duty_id are required' };
  }
  onProgress?.(DEFAULT_COEFFICIENTS.coil_rate); // coil_rate coefficient
  return {
    model: null,
    error: null,
    input: {
      eadb_temp: section.duty_id === 1 ? 80.0 : 45.0,
      eawb_temp: section.duty_id === 1 ? 67.0 : undefined,
      ladb_temp: section.duty_id === 1 ? 55.0 : 85.0,
      lawb_temp: section.duty_id === 1 ? 54.0 : undefined,
    },
  };
}
